import { ArrowRight, BookOpen, LifeBuoy, Wrench } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/modules/automation/ui';
import {
  findDoc,
  PLAYBOOK_DOCS,
  searchScore,
  TEMPLATE_DOCS,
  TROUBLESHOOTING,
  type DocKind,
  type DocPage,
} from '@/modules/docs/content';
import { DocView } from '@/modules/docs/DocView';
import { helpLink } from '@/modules/docs/links';
import { ReferenceIndex } from '@/modules/docs/ReferenceIndex';
import { GuideSteps } from '@/modules/marketing/GuideSteps';
import { GUIDES, guideBySlug, type Guide } from '@/modules/marketing/guides';
import { NotFoundPage } from '@/modules/system/NotFoundPage';

/**
 * Docs, inside the dashboard.
 *
 * The same pages as the public documentation, without leaving the product: the task guides, a page
 * for every playbook and template with every option, and the fixes for common errors. A customer in
 * the middle of a run reads the page for the playbook they are running, and the support queue gets
 * the questions the pages do not answer.
 */

interface SearchHit {
  title: string;
  summary: string;
  to: string;
  kind: string;
}

function guideScore(guide: Guide, query: string): number {
  const text = `${guide.title} ${guide.summary} ${guide.steps.map((step) => `${step.title} ${step.body}`).join(' ')}`.toLowerCase();
  const terms = query.toLowerCase().split(/\s+/).filter((term) => term.length > 1);

  if (terms.length === 0 || terms.some((term) => !text.includes(term))) {
    return 0;
  }

  return terms.reduce((score, term) => score + (guide.title.toLowerCase().includes(term) ? 8 : 1), 0);
}

function kindLabel(kind: DocKind): string {
  return kind === 'playbook' ? 'Playbook' : kind === 'template' ? 'Template' : 'Troubleshooting';
}

function docPath(page: DocPage): string {
  return page.kind === 'troubleshooting' ? '/help/troubleshooting' : `/help/${page.kind}s/${page.slug}`;
}

export function HelpHomePage() {
  const searchId = useId();
  const [query, setQuery] = useState('');
  const trimmed = query.trim();

  const hits = useMemo<SearchHit[]>(() => {
    if (trimmed.length < 2) {
      return [];
    }

    const pages = [...PLAYBOOK_DOCS, ...TEMPLATE_DOCS, ...(TROUBLESHOOTING === undefined ? [] : [TROUBLESHOOTING])];

    return [
      ...pages.map((page) => ({ score: searchScore(page, trimmed), hit: { title: page.title, summary: page.summary, to: docPath(page), kind: kindLabel(page.kind) } })),
      ...GUIDES.map((guide) => ({ score: guideScore(guide, trimmed), hit: { title: guide.title, summary: guide.summary, to: `/help/guides/${guide.slug}`, kind: 'Guide' } })),
    ]
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.hit);
  }, [trimmed]);

  return (
    <div className="space-y-10">
      <PageHeader
        title="Docs"
        description="Step-by-step guides, every playbook and template with each of its options, and the fixes for common errors."
      />

      <div className="max-w-2xl space-y-2">
        <label htmlFor={searchId} className="text-sm font-medium">
          Search the docs
        </label>
        <Input
          id={searchId}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete="off"
          spellCheck={false}
        />
        <p className="text-muted-foreground text-xs">
          Search by task, playbook, option or the words of an error message.
        </p>
      </div>

      {trimmed.length >= 2 ? (
        <section aria-live="polite" className="space-y-3">
          <h2 className="text-lg font-semibold tracking-tight">
            {hits.length === 0 ? 'Nothing matches that search' : `${hits.length} result${hits.length === 1 ? '' : 's'}`}
          </h2>
          {hits.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              Try fewer words, or the name of the playbook. If the answer is not here,{' '}
              <Link to="/support" className="text-primary hover:underline">
                open a support ticket
              </Link>
              .
            </p>
          ) : (
            <ul className="space-y-2">
              {hits.map((hit) => (
                <li key={hit.to}>
                  <Link
                    to={hit.to}
                    className="border-border/60 hover:border-primary/40 hover:bg-muted/30 flex items-start justify-between gap-4 rounded-xl border p-4 transition-colors"
                  >
                    <span className="min-w-0">
                      <span className="text-foreground block font-medium">{hit.title}</span>
                      <span className="text-muted-foreground mt-1 block text-sm">{hit.summary}</span>
                    </span>
                    <span className="text-muted-foreground shrink-0 text-xs">{hit.kind}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <>
          <section id="guides" className="scroll-mt-24 space-y-4">
            <SectionTitle icon={BookOpen} title="Guides" text="One task each, with every control named as it appears in the dashboard." />
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {GUIDES.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/help/guides/${guide.slug}`}
                  className="border-border/60 hover:border-primary/40 hover:bg-muted/30 group rounded-xl border p-4 transition-colors"
                >
                  <span className="text-foreground flex items-center gap-2 font-medium">
                    <guide.icon className="text-primary size-4" aria-hidden />
                    {guide.title}
                    <ArrowRight className="size-4 opacity-0 transition-opacity group-hover:opacity-70" aria-hidden />
                  </span>
                  <span className="text-muted-foreground mt-1 block text-sm leading-relaxed">{guide.summary}</span>
                </Link>
              ))}
            </div>
          </section>

          <section id="troubleshooting" className="scroll-mt-24 space-y-4">
            <SectionTitle icon={LifeBuoy} title="Troubleshooting" text="What each error message means, and what to do about it." />
            <Link
              to="/help/troubleshooting"
              className="border-border/60 hover:border-primary/40 hover:bg-muted/30 flex max-w-2xl items-start justify-between gap-4 rounded-xl border p-4 transition-colors"
            >
              <span>
                <span className="text-foreground block font-medium">{TROUBLESHOOTING?.title ?? 'Common Errors and How to Fix Them'}</span>
                <span className="text-muted-foreground mt-1 block text-sm">{TROUBLESHOOTING?.summary}</span>
              </span>
              <ArrowRight className="text-muted-foreground mt-1 size-4 shrink-0" aria-hidden />
            </Link>
          </section>

          <section id="playbooks" className="scroll-mt-24 space-y-4">
            <SectionTitle icon={Wrench} title="Playbooks" text="What each playbook does, every option explained, and its errors." />
            <ReferenceIndex kind="playbook" linkFor={helpLink} />
          </section>

          <section id="templates" className="scroll-mt-24 space-y-4">
            <SectionTitle icon={Wrench} title="Templates" text="What each template builds, its steps, and what to do after the run." />
            <ReferenceIndex kind="template" linkFor={helpLink} />
          </section>
        </>
      )}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, text }: { icon: typeof BookOpen; title: string; text: string }) {
  return (
    <div>
      <h2 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
        <Icon className="text-primary size-5" aria-hidden />
        {title}
      </h2>
      <p className="text-muted-foreground mt-1 text-sm">{text}</p>
    </div>
  );
}

/** A playbook, template or the troubleshooting page, in the dashboard. */
export function HelpDocPage({ kind }: { kind: DocKind }) {
  const { key } = useParams<{ key: string }>();
  const page = kind === 'troubleshooting' ? TROUBLESHOOTING : findDoc(kind, key ?? '');

  if (page === undefined) {
    return <NotFoundPage />;
  }

  const action =
    kind === 'playbook' ? (
      <Link
        to={`/automation/playbooks/${page.slug}`}
        className="bg-primary text-primary-foreground inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium hover:opacity-90"
      >
        Open in Playbooks
        <ArrowRight className="size-4" aria-hidden />
      </Link>
    ) : kind === 'template' ? (
      <Link
        to={`/automation/templates/${page.slug}`}
        className="bg-primary text-primary-foreground inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium hover:opacity-90"
      >
        Open in Templates
        <ArrowRight className="size-4" aria-hidden />
      </Link>
    ) : undefined;

  return (
    <div className="space-y-6">
      <Link to={kind === 'troubleshooting' ? '/help' : `/help#${kind}s`} className="text-muted-foreground hover:text-foreground text-[13px]">
        ← Docs
      </Link>
      <div className="max-w-4xl">
        <DocView page={page} linkFor={helpLink} action={action} />
      </div>
    </div>
  );
}

/** A task guide, in the dashboard. */
export function HelpGuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const guide = guideBySlug(slug ?? '');

  if (guide === undefined) {
    return <NotFoundPage />;
  }

  // An old slug for a renamed guide lands on the current address.
  if (guide.slug !== slug) {
    return <Navigate to={`/help/guides/${guide.slug}`} replace />;
  }

  return (
    <div className="space-y-6">
      <Link to="/help#guides" className="text-muted-foreground hover:text-foreground text-[13px]">
        ← Docs
      </Link>
      <div className="max-w-4xl">
        <p className="text-primary text-xs font-semibold tracking-wider uppercase">Guide</p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">{guide.title}</h1>
        <p className="text-muted-foreground mt-2 leading-relaxed">{guide.summary}</p>
        <p className="border-border/60 bg-muted/20 mt-5 rounded-lg border px-4 py-3 text-sm">
          <span className="font-medium">Before you start: </span>
          <span className="text-muted-foreground">{guide.before}</span>
        </p>
        <GuideSteps guide={guide} className="mt-10" />
      </div>
    </div>
  );
}
